import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode,
} from "react";
import { ApiError } from "./api";

export type ToastKind = "error" | "success" | "info";

export interface Toast {
  id: number;
  kind: ToastKind;
  title: string;
  detail?: string;
}

interface ToastActions {
  notify: (toast: Omit<Toast, "id">) => void;
  success: (title: string, detail?: string) => void;
  info: (title: string, detail?: string) => void;
  /** Turns anything thrown into a readable message. */
  reportError: (error: unknown, fallback?: string) => void;
  dismiss: (id: number) => void;
}

/**
 * Two contexts on purpose. The actions never change, so every screen that raises a toast
 * (and every callback that lists `toast` as a dependency) stays put when one appears or
 * expires; only the rack reads the list.
 */
const ToastActionsContext = createContext<ToastActions | null>(null);
const ToastListContext = createContext<Toast[]>([]);
const LIFETIME_MS = 7000;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(1);
  const timers = useRef(new Map<number, ReturnType<typeof setTimeout>>());

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    const timer = timers.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timers.current.delete(id);
    }
  }, []);

  const notify = useCallback((toast: Omit<Toast, "id">) => {
    const id = nextId.current++;
    setToasts((prev) => [...prev.slice(-3), { ...toast, id }]);
    timers.current.set(id, setTimeout(() => dismiss(id), LIFETIME_MS));
  }, [dismiss]);

  useEffect(() => () => {
    for (const timer of timers.current.values()) clearTimeout(timer);
    timers.current.clear();
  }, []);

  const actions = useMemo<ToastActions>(() => ({
    notify,
    success: (title, detail) => notify({ kind: "success", title, detail }),
    info: (title, detail) => notify({ kind: "info", title, detail }),
    reportError: (error, fallback = "Something went wrong.") => {
      const title = error instanceof ApiError
        ? error.message
        : error instanceof Error && error.message
          ? error.message
          : fallback;
      notify({ kind: "error", title });
    },
    dismiss,
  }), [notify, dismiss]);

  return (
    <ToastActionsContext.Provider value={actions}>
      {children}
      <ToastListContext.Provider value={toasts}>
        <ToastRack />
      </ToastListContext.Provider>
    </ToastActionsContext.Provider>
  );
}

function ToastRack() {
  const toasts = useContext(ToastListContext);
  const { dismiss: onDismiss } = useToast();
  return (
    <div className="toasts" role="region" aria-label="Notifications">
      <div aria-live="assertive" aria-atomic="false" style={{ display: "contents" }}>
        {toasts.map((toast) => (
          <div key={toast.id} className={`toast toast-${toast.kind}`}>
            <div>
              <strong>{toast.title}</strong>
              {toast.detail ? <p>{toast.detail}</p> : null}
            </div>
            <button
              type="button"
              className="btn btn-quiet btn-sm"
              onClick={() => onDismiss(toast.id)}
              aria-label="Dismiss notification"
            >
              ✕
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

export function useToast(): ToastActions {
  const value = useContext(ToastActionsContext);
  if (!value) throw new Error("useToast must be used inside a ToastProvider");
  return value;
}
